import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { TimelineService } from '../../../../services/timeline.service';
import { AuthService } from '../../../../services/auth.service';
import { AssetListSharedState, AssetListStateService } from '../../services/asset-list-state.service';
import { AssetListDragDropService } from '../../services/drag-drop.service';
import { AssetSelectionService } from '../../services/asset-selection.service';
import { AssetOperationsService } from '../../services/asset-operations.service';
import { AssetListVariantService } from '../../services/variant.service';
import { AssetListUiHelperService } from '../../services/ui-helper.service';
import { Asset, DropdownController, SubAsset } from '../../models/asset.model';
import { AssetListColor } from '../../../../../layouts/my-projects/project.const';

@Component({
  selector: 'mtm-asset-card',
  templateUrl: './asset-card.component.html',
  styleUrl: './asset-card.component.scss',
  standalone: false,
})
export class AssetCardComponent implements OnInit {
  @Input() content: Asset;
  @Input() funnel: string;
  @Input() asSelector: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() isProjectSetting: boolean = false;
  @Input() showStatus: boolean = false;
  @Input() project: any;
  @Input() projectColor: typeof AssetListColor = AssetListColor;
  @Input() projectStatus: any = {};
  @Input() bundle: Asset[] = [];
  @Input() originalBundle: Asset[] = [];
  @Input() assetStar: string;
  @Input() campaignId: string;
  @Input() projectId: string;
  @Input() asWidget: boolean = false;
  @Input() multiple: boolean = false;
  @Input() fromCampaignDashboard: boolean = false;
  @Input() ngUnsubscribe: Subject<void>;

  @Output() assetSelected: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() assetUpdated: EventEmitter<any> = new EventEmitter<any>();
  @Output() assetDeleted: EventEmitter<any> = new EventEmitter<any>();
  @Output() assetDeletedSubAsset: EventEmitter<any> = new EventEmitter<any>();
  @Output() bulkUpdateAssets: EventEmitter<any> = new EventEmitter<any>();
  @Output() dragAndDropAssets: EventEmitter<any> = new EventEmitter<any>();
  @Output() bundleChange: EventEmitter<{ newBundle: any[]; newOriginalBundle: any[] }> = new EventEmitter<{
    newBundle: Asset[];
    newOriginalBundle: Asset[];
  }>();
  @Output() updateTimelineAndProject: EventEmitter<any> = new EventEmitter<any>();
  @Output() getLinkedAssets: EventEmitter<void> = new EventEmitter<void>();
  @Output() dropdownHover: EventEmitter<{ dropdown: DropdownController; action: 'enter' | 'leave' }> =
    new EventEmitter<{
      dropdown: DropdownController;
      action: 'enter' | 'leave';
    }>();

  isCreator: boolean = false;

  constructor(
    private readonly timelineService: TimelineService,
    private readonly authService: AuthService,
    public readonly stateService: AssetListStateService,
    private readonly assetListDragDropService: AssetListDragDropService,
    private readonly assetSelectionService: AssetSelectionService,
    private readonly assetOperationsService: AssetOperationsService,
    private readonly assetListVariantService: AssetListVariantService,
    private readonly assetListUiHelperService: AssetListUiHelperService
  ) {}

  get state(): AssetListSharedState {
    return this.stateService.currentState;
  }

  get isEditing(): boolean {
    return this.state.isEditing;
  }

  get isBusy(): boolean {
    return this.state.isBusy;
  }

  get draggedCard(): Asset | null {
    return this.state.draggedCard;
  }

  get targetCardId(): string | null {
    return this.state.targetCardId;
  }

  get affectedAssets(): Record<string, { subAsset: SubAsset[]; status?: string }> {
    return this.state.affectedAssets;
  }

  get selectedAssetTypes(): string[] {
    return this.state.selectedAssetTypes;
  }

  get projects(): any {
    return this.state.projects;
  }

  get cacheBuster(): string {
    return this.state.cacheBuster;
  }

  get first_awarness_asset(): string {
    return this.state.first_awarness_asset;
  }

  public ngOnInit(): void {
    const authUser: any = this.authService.getAuthUser();
    this.isCreator = authUser?.email === this.content?.createdBy;
  }

  public onDragStart(content: Asset): void {
    this.assetListDragDropService.onDragStart(content);
  }

  public onDragOver(event: DragEvent, id: string): void {
    this.assetListDragDropService.onDragOver(event, id);
  }

  public onDragLeave(): void {
    this.assetListDragDropService.onDragLeave();
  }

  public onDrop(content: Asset): void {
    this.assetListDragDropService.onDrop(
      content,
      this.bundle,
      (dragAsset: Asset, dropAsset: Asset) => {
        const newBundle: Asset[] = this.assetListDragDropService.doMoveAsset(
          dragAsset,
          dropAsset,
          this.bundle,
          (data: { dragAssetId: string; dropAssetId: string }) => this.dragAndDropAssets.emit(data),
          (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle })
        );
        if (newBundle) {
          this.bundleChange.emit({ newBundle, newOriginalBundle: this.originalBundle });
        }
      },
      true
    );
  }

  public onDragEnd(): void {
    this.assetListDragDropService.onDragEnd();
  }

  public selectAsset(event: { target: { checked: boolean } }, content: Asset): void {
    this.assetSelectionService.selectAsset(
      event,
      content,
      this.fromCampaignDashboard,
      this.multiple,
      (selectedAssetTypes: string[]) => this.assetSelected.emit(selectedAssetTypes),
      () => this.getLinkedAssets.emit(),
      this.ngUnsubscribe
    );
  }

  public selecteAsset(id: string): void {
    this.assetSelectionService.assetToggle(id);
  }

  public openEditAssetModal(event: { funnel: string; content: any; isVariant?: boolean }): void {
    this.assetOperationsService
      .openEditAssetModal(
        event.funnel,
        event.content,
        this.bundle,
        this.originalBundle,
        () => structuredClone(this.state.allAssets),
        event.isVariant || false,
        this.projectId,
        this.asWidget,
        (result: Asset) => this.assetUpdated.emit(result),
        (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle }),
        (data: { allBundle: Asset[]; newAsset?: Asset }) => this.updateTimelineAndProject.emit(data)
      )
      ?.then((result: { newBundle: Asset[]; newOriginalBundle: Asset[] } | null) => {
        if (result) {
          this.bundleChange.emit(result);
        }
      });
  }

  public duplicateAsset(content: Asset): void {
    const result: { newBundle: Asset[]; newOriginalBundle: Asset[] } | null =
      this.assetOperationsService.duplicateAsset(
        content,
        this.assetStar,
        this.bundle,
        this.originalBundle,
        this.projectId,
        this.asWidget,
        (data: { action: string; assetIds: string[] }) => this.bulkUpdateAssets.emit(data),
        (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle }),
        (data: any) => this.updateTimelineAndProject.emit(data)
      );

    if (result) {
      this.bundleChange.emit(result);
    }
  }

  public hideAsset(content: Asset): void {
    const result: { newBundle: Asset[]; newOriginalBundle: Asset[] } | null = this.assetOperationsService.hideAsset(
      content,
      this.bundle,
      this.originalBundle,
      (updatedAsset: Asset) => this.assetUpdated.emit(updatedAsset),
      (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle })
    );

    if (result) {
      this.bundleChange.emit(result);
    }
  }

  public deleteAsset(content: Asset): void {
    this.assetOperationsService
      .deleteAsset(
        content,
        this.bundle,
        this.originalBundle,
        (deletedAsset: Asset) => this.assetDeleted.emit(deletedAsset),
        (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle })
      )
      ?.then((result: { newBundle: Asset[]; newOriginalBundle: Asset[] } | null) => {
        if (result) {
          this.bundleChange.emit(result);
        }
      });
  }

  public onMoveToModalOpen(content: Asset): void {
    this.assetListDragDropService.onMoveToModalOpen(content, this.bundle, this.campaignId, (target: Asset) => {
      this.assetListDragDropService.onDrop(
        target,
        this.bundle,
        (dragAsset: Asset, dropAsset: Asset) => {
          const newBundle: Asset[] = this.assetListDragDropService.doMoveAsset(
            dragAsset,
            dropAsset,
            this.bundle,
            (data: { dragAssetId: string; dropAssetId: string }) => this.dragAndDropAssets.emit(data),
            (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle })
          );
          if (newBundle) {
            this.bundleChange.emit({ newBundle, newOriginalBundle: this.originalBundle });
          }
        },
        false
      );
    });
  }

  public changeProjectStatus(asset: Asset & { deliveryDate: string }, status: string): void {
    this.assetListVariantService.updateStatus(asset, status, this.project, this.projectId);
  }

  public changeProjectVarientStatus(content: Asset, variantId: string, status: string): void {
    this.assetListVariantService.changeProjectVarientStatus(content, variantId, status, this.project, this.projectId);
  }

  public duplicateVariant(event: { content: Asset; variant: SubAsset }): void {
    const result: { newBundle: Asset[]; newOriginalBundle: Asset[] } | null =
      this.assetListVariantService.duplicateVariant(
        event.content,
        event.variant,
        this.bundle,
        this.originalBundle,
        this.projectId,
        this.asWidget,
        (updatedContent: Asset) => this.assetUpdated.emit(updatedContent),
        (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle }),
        (data: any) => this.updateTimelineAndProject.emit(data)
      );

    if (result) {
      this.bundleChange.emit(result);
    }
  }

  public deleteVariant(event: { content: Asset; variant: SubAsset }): void {
    this.assetListVariantService
      .deleteVariant(
        event.content,
        event.variant,
        this.bundle,
        this.originalBundle,
        (data: { id: string; subAssetId: string }) => this.assetDeletedSubAsset.emit(data),
        (bundle: Asset[]) => this.bundleChange.emit({ newBundle: bundle, newOriginalBundle: this.originalBundle })
      )
      ?.then((result: { newBundle: Asset[]; newOriginalBundle: Asset[] } | null) => {
        if (result) {
          this.bundleChange.emit(result);
        }
      });
  }

  public over(dropdown: DropdownController): void {
    this.dropdownHover.emit({ dropdown, action: 'enter' });
  }

  public out(dropdown: DropdownController): void {
    this.dropdownHover.emit({ dropdown, action: 'leave' });
  }

  public formatNgbDate(date: { year: number; month: number; day: number }): string {
    if (!date) return '';

    const day: string = date.day.toString().padStart(2, '0');
    const month: string = date.month.toString().padStart(2, '0');
    const { year } = date;

    return `${day}.${month}.${year}`;
  }

  public convertToDateObject(timestamp: string | number | { year: number; month: number; day: number }): {
    year: number;
    month: number;
    day: number;
  } {
    if (!timestamp) return { year: 0, month: 0, day: 0 };

    if (typeof timestamp === 'object' && 'year' in timestamp && 'month' in timestamp && 'day' in timestamp) {
      return timestamp;
    }

    const date: Date = new Date(Number(timestamp));
    if (Number.isNaN(date.getTime())) return { year: 0, month: 0, day: 0 };

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  public getVariantStatus(affectedAsset: Asset & { deliveryDate: string }, variantId: string): string {
    return this.assetListVariantService.getVariantStatus(affectedAsset, variantId);
  }

  public getLengthLabel(val: string): string {
    return this.assetListUiHelperService.getLengthLabel(val);
  }

  public getColorFromName(name: string): string {
    return this.timelineService.getColorFromName(name);
  }
}
